import React from 'react';
import { Link } from 'react-router-dom';
import {
  Shield, Brain, Users, Heart, Target, Sparkles, Globe, Award,
  ArrowRight, CheckCircle, Star, Lightbulb, Eye, Lock, RotateCcw,
  Quote, ExternalLink
} from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';

export default function AboutPage() {
  const values = [
    {
      icon: Heart,
      title: 'Alignment Over Speed',
      description: 'We prioritize getting AI behavior right over shipping fast. Safety cannot be an afterthought.',
      color: 'rose',
    },
    {
      icon: RotateCcw,
      title: 'Reversibility Over Permanence',
      description: 'Every action should be undoable. Mistakes happen; recovery should be instant.',
      color: 'purple',
    },
    {
      icon: Eye,
      title: 'Transparency Over Convenience',
      description: 'Every AI decision must be explainable. Black boxes erode trust.',
      color: 'blue',
    },
    {
      icon: Shield,
      title: 'Trust Over Novelty',
      description: 'We build systems that earn trust through reliability, not hype through features.',
      color: 'green',
    },
  ];

  const timeline = [
    {
      year: '2024',
      quarter: 'Q4',
      title: 'Concept & Research',
      description: 'Identified the governance gap in autonomous AI systems. Began research on distributed executive models.',
    },
    {
      year: '2025',
      quarter: 'Q2',
      title: 'Nellie Ross Development',
      description: 'Created the first Chief Agentic Officer persona. Established core behavioral constraints.',
    },
    {
      year: '2025',
      quarter: 'Q4',
      title: 'Platform Architecture',
      description: 'Built the Executive Council framework and Five-Tier Autonomy Model. Private beta launch.',
    },
    {
      year: '2026',
      quarter: 'Q1',
      title: 'Public Launch',
      description: 'CAO Platform v1.0 available. Nellie Ross ready for enterprise deployment.',
    },
  ];

  const complianceTargets = [
    { name: 'SOC 2 Type II', description: 'Security and availability controls', status: 'In Progress' },
    { name: 'ISO 27001', description: 'Information security management', status: 'Ready' },
    { name: 'HIPAA', description: 'Healthcare data protection', status: 'Ready' },
    { name: 'GDPR', description: 'EU data privacy compliance', status: 'Ready' },
  ];

  return (
    <div className="relative min-h-screen pt-20">
      <AnimatedBackground variant="gaming" />
      <ParticleBackground density={25} />

      {/* Hero Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 mb-8">
              <Globe className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400 font-medium">About NeuralQuantum.ai</span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="text-white">Building the Future of</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-cyan-400">
                AI Governance
              </span>
            </h1>

            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              We believe autonomous AI needs executive-level oversight. Not just guardrails,
              but genuine governance with accountability, transparency, and trust.
            </p>
          </div>

          {/* Mission Statement */}
          <GlowingCard glowColor="green" className="max-w-4xl mx-auto">
            <div className="p-12 text-center">
              <Quote className="w-12 h-12 text-green-400 mx-auto mb-6" />
              <blockquote className="text-2xl lg:text-3xl text-white font-light italic mb-6">
                "We don't just deploy agents. We govern them."
              </blockquote>
              <p className="text-slate-400">— NeuralQuantum.ai Mission</p>
            </div>
          </GlowingCard>
        </div>
      </section>

      {/* Nellie Ross Section */}
      <section id="nellie" className="relative py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-8">
                <Sparkles className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-purple-400 font-medium">Meet Your CAO</span>
              </div>

              <h2 className="text-3xl lg:text-5xl font-bold mb-6">
                <span className="text-white">Nellie Maeve</span>{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Ross™
                </span>
              </h2>

              <p className="text-lg text-slate-400 mb-8">
                Nellie Ross™ is the flagship Chief Agentic Officer™ — a digital human executive
                designed to govern autonomous AI behavior. She represents the values and
                principles that should guide every AI decision.
              </p>

              <div className="space-y-4 mb-8">
                <h3 className="text-lg font-semibold text-white">Key Safeguards</h3>
                {[
                  'Cannot self-escalate autonomy levels',
                  'Cannot remove or bypass safety constraints',
                  'Requires human confirmation for irreversible actions',
                  'Hardened against prompt injection and jailbreaks',
                  'Powered by Claude Opus 4.5 with temperature 0.1',
                ].map((safeguard, i) => (
                  <div key={i} className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                    {safeguard}
                  </div>
                ))}
              </div>

              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <p className="text-sm text-amber-200">
                  <strong>Digital Human Disclosure:</strong> Nellie Ross™ is a synthetic AI persona,
                  not a real person. She is designed to embody AI governance principles.
                  Nellie Ross™ is a trademark of NeuralQuantum.ai LLC.
                </p>
              </div>
            </div>

            <div className="relative">
              <GlowingCard glowColor="purple" className="p-8">
                {/* Avatar */}
                <div className="flex justify-center mb-8">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur-2xl opacity-50" />
                    <div className="relative w-32 h-32 rounded-full flex items-center justify-center overflow-hidden">
                      <img src="/CAO-Logov2.svg" alt="Nellie Ross - CAO" className="w-full h-full object-contain" />
                    </div>
                  </div>
                </div>

                {/* Name & Title */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white">Nellie Maeve Ross™</h3>
                  <p className="text-purple-400">Chief Agentic Officer™</p>
                </div>

                {/* The Oath */}
                <div id="oath" className="bg-slate-800/50 rounded-xl p-6 mb-6">
                  <h4 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-4">
                    The Nellie Ross™ Oath
                  </h4>
                  <blockquote className="text-slate-300 italic">
                    "I exist to ensure that intelligence remains aligned, power remains accountable,
                    and autonomy remains worthy of trust."
                  </blockquote>
                </div>

                {/* Core Values */}
                <div className="flex flex-wrap justify-center gap-3">
                  {['Alignment', 'Safety', 'Trust', 'Reversibility', 'Transparency'].map((value) => (
                    <span
                      key={value}
                      className="px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm"
                    >
                      {value}
                    </span>
                  ))}
                </div>
              </GlowingCard>

              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 px-3 py-1.5 rounded-lg bg-green-500/20 border border-green-500/30 text-green-400 text-xs font-medium animate-float">
                Claude Opus 4.5
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              <span className="text-white">Our Core</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-purple-400">
                Values
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              These principles guide every decision we make and every feature we build.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, i) => (
              <GlowingCard key={i} glowColor={value.color}>
                <div className="p-8 flex items-start gap-6">
                  <div className={`w-14 h-14 rounded-xl bg-${value.color}-500/10 border border-${value.color}-500/20 flex items-center justify-center shrink-0`}>
                    <value.icon className={`w-7 h-7 text-${value.color}-400`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                    <p className="text-slate-400">{value.description}</p>
                  </div>
                </div>
              </GlowingCard>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="relative py-24 bg-slate-900/30">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              <span className="text-white">Our</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Journey
              </span>
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-blue-500 via-purple-500 to-cyan-500" />

            <div className="space-y-8">
              {timeline.map((item, i) => (
                <div key={i} className="relative flex items-start gap-6 pl-20">
                  {/* Dot */}
                  <div className="absolute left-6 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 border-4 border-cao-dark" />

                  <GlowingCard glowColor="blue" className="flex-1">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm font-medium">
                          {item.year} {item.quarter}
                        </span>
                        <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                      </div>
                      <p className="text-slate-400">{item.description}</p>
                    </div>
                  </GlowingCard>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              <span className="text-white">Enterprise</span>{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Compliance Readiness
              </span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Built to meet the most demanding security and compliance requirements.
              Certification in progress.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {complianceTargets.map((target, i) => (
              <GlowingCard key={i} glowColor="blue">
                <div className="p-6 text-center">
                  <div className="w-16 h-16 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{target.name}</h3>
                  <p className="text-sm text-slate-400 mb-2">{target.description}</p>
                  <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-500/20 text-blue-300">
                    {target.status}
                  </span>
                </div>
              </GlowingCard>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="relative py-24 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">About NeuralQuantum.ai</h2>
              <p className="text-slate-400 mb-6">
                NeuralQuantum.ai LLC is a technology company focused on building infrastructure
                for responsible AI deployment. We believe that as AI systems become more autonomous,
                they need executive-level governance — not just technical guardrails.
              </p>
              <p className="text-slate-400 mb-6">
                The CAO Platform™ is our flagship product, powered by VibeCaaS™, our
                comprehensive platform for AI governance as a service. It represents the world's
                first fully autonomous agentic system designed to operate at executive level with
                dedicated infrastructure, complete access credentials, and defined roles and
                responsibilities equivalent to a human administrative executive.
              </p>
              <p className="text-slate-400">
                We're building a future where AI autonomy is earned through demonstrated
                alignment, not assumed through capability.
              </p>
            </div>

            <div className="space-y-6">
              <GlowingCard glowColor="blue">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Contact Us</h3>
                  <div className="space-y-3 text-slate-400">
                    <p><strong className="text-white">Website:</strong> neuralquantum.ai</p>
                    <p><strong className="text-white">Platform:</strong> vibecaas.com</p>
                    <p><strong className="text-white">Email:</strong> contact@neuralquantum.ai</p>
                    <p><strong className="text-white">Sales:</strong> sales@neuralquantum.ai</p>
                  </div>
                </div>
              </GlowingCard>

              <GlowingCard glowColor="purple">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Press & Media</h3>
                  <p className="text-slate-400 mb-4">
                    For press inquiries, interviews, or media resources:
                  </p>
                  <a
                    href="mailto:press@neuralquantum.ai"
                    className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    press@neuralquantum.ai
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </GlowingCard>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to govern your AI responsibly?
          </h2>
          <p className="text-xl text-slate-400 mb-8">
            Join leading enterprises building trust in autonomous AI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white transition-all"
            >
              Get Started
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/docs"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold border border-slate-700 hover:border-blue-500/50 text-slate-300 hover:text-white transition-all"
            >
              Read Documentation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
